/* eslint-disable no-unused-vars */
import { useCallback, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  useForm as useHookForm,
  DeepPartial,
  Path,
  KeepStateOptions,
  FieldValues,
} from 'react-hook-form'

interface IUseForm<T> {
  isEditing?: boolean
  defaultValues?: DeepPartial<T>
  schema: any
  onSubmit: (v: T) => void
}

const resetFormOpts: KeepStateOptions = {
  keepDirtyValues: false,
  keepErrors: false,
  keepDirty: false,
  keepValues: false,
  keepDefaultValues: false,
  keepIsSubmitted: false,
  keepTouched: false,
  keepIsValid: false,
  keepSubmitCount: false,
}

export const useForm = <T extends FieldValues>({
  isEditing,
  defaultValues,
  schema,
  onSubmit,
}: IUseForm<T>) => {
  const {
    trigger,
    setValue,
    reset,
    control,
    handleSubmit,
    clearErrors,
    getValues,
    formState: { errors },
    watch,
  } = useHookForm<T>({
    resolver: yupResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues,
  })

  const onTriggerChange = useCallback(
    (f: Path<T>, v: any) => {
      setValue(f, v)
      trigger(f)
    },
    [setValue, trigger],
  )

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues, resetFormOpts)
    }
  }, [defaultValues, reset])

  useEffect(() => {
    if (!isEditing) clearErrors()
  }, [clearErrors, isEditing])

  return {
    control,
    errors,
    clearErrors,
    isValid: Object.keys(errors).length === 0,
    onTriggerChange,
    setValue,
    trigger,
    reset,
    getValues,
    watch,
    onFormSubmit: handleSubmit(onSubmit, err =>
      console.error('Form Error', err),
    ),
  }
}
